import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

import { useAuthStore } from '@/stores/authStore';
import { previewRequestGuard } from '@/lib/previewGuard';
import { SessionExpiredError, endSession, refreshAccessToken } from '@/lib/authRefresh';

const API_BASE = import.meta.env.VITE_API_URL || '';

export const apiClient = axios.create({
  baseURL: API_BASE,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true, // send httpOnly cookies
});

// Public endpoints that should NOT send the Authorization header
const publicPaths = ['/api/v1/auth/login', '/api/v1/auth/register', '/api/v1/auth/setup', '/api/v1/auth/forgot-password', '/api/v1/auth/reset-password'];

// Block writes while "preview as student" is active (see previewGuard.ts)
apiClient.interceptors.request.use(previewRequestGuard);

// Attach access token to every request (except public auth endpoints)
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const isPublic = publicPaths.some((p) => config.url?.includes(p));
  if (!isPublic) {
    const token = useAuthStore.getState().accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Handle 401: attempt silent refresh, then retry original request once.
// Refresh is shared within the tab and serialised across tabs (authRefresh.ts);
// refresh tokens rotate on every use, so a redundant refresh would race.
apiClient.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status !== 401 || !originalRequest || originalRequest._retry) {
      return Promise.reject(error);
    }
    originalRequest._retry = true;

    // Sent with a token that has since been refreshed (it was in flight while
    // another request refreshed): just retry — the request interceptor
    // attaches the current token. Refreshing again would rotate needlessly.
    const sentToken = String(originalRequest.headers.Authorization ?? '').replace(/^Bearer /, '');
    const currentToken = useAuthStore.getState().accessToken;
    if (currentToken && sentToken && sentToken !== currentToken) {
      return apiClient(originalRequest);
    }

    try {
      await refreshAccessToken();
    } catch (refreshError) {
      // Only a definitive 401 from refresh ends the session. Anything else
      // (network drop, timeout, API restarting) fails this one request and
      // keeps the user signed in — logging out on those was what kicked
      // students out mid-lesson and threw away whatever they were typing.
      if (refreshError instanceof SessionExpiredError) endSession();
      return Promise.reject(error);
    }
    return apiClient(originalRequest);
  }
);
